import React, { useState } from "react";
import styled from "styled-components";
import { motion } from "framer-motion";

/* ================= CONSTANTS ================= */

const BRAND_GRADIENT = "linear-gradient(135deg, #0047ff, #00c4ff)";
const WHATSAPP_NUMBER = "918905578564";

/* ================= WRAPPER ================= */

const Wrapper = styled.div`
  font-family: "Poppins", sans-serif;
  min-height: 100vh;
  color: #e5e7eb;
  padding: 80px 16px;
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

/* ================= HERO ================= */

const Section = styled(motion.section)`
  text-align: center;
  padding: 40px 20px;
`;

const Title = styled.h1`
  font-size: 2.4rem;
  margin-bottom: 16px;
  background: ${BRAND_GRADIENT};
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
`;

const Paragraph = styled.p`
  max-width: 820px;
  margin: 0 auto;
  font-size: 1.1rem;
  color: #cbd5e1;
`;

/* ================= JOB CARDS ================= */

const JobsGrid = styled.div`
  margin-top: 60px;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: 24px;
`;

const JobCard = styled(motion.div)`
  background: #0f172a;
  padding: 24px;
  border-radius: 16px;
  border: 1px solid #1e293b;
`;

const JobTitle = styled.h3`
  color: #fff;
  margin-bottom: 10px;
`;

const JobDesc = styled.p`
  font-size: 0.95rem;
  color: #cbd5e1;
  margin-bottom: 16px;
`;

const BtnRow = styled.div`
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
`;

const ApplyBtn = styled.button`
  padding: 10px 18px;
  border-radius: 30px;
  border: none;
  cursor: pointer;
  background: ${BRAND_GRADIENT};
  color: #fff;
  font-size: 0.85rem;
`;

const WhatsAppBtn = styled.a`
  padding: 10px 18px;
  border-radius: 30px;
  background: #22c55e;
  color: #fff;
  text-decoration: none;
  font-size: 0.85rem;
`;

/* ================= MODAL ================= */

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 999;
`;

const Modal = styled.div`
  background: #020617;
  padding: 28px;
  border-radius: 18px;
  width: 100%;
  max-width: 460px;
  border: 1px solid #1e293b;
`;

const Input = styled.input`
  width: 100%;
  padding: 12px;
  margin-bottom: 14px;
  border-radius: 10px;
  border: none;
  background: #0f172a;
  color: #fff;
`;

const Select = styled.select`
  width: 100%;
  padding: 12px;
  margin-bottom: 14px;
  border-radius: 10px;
  border: none;
  background: #0f172a;
  color: #fff;
`;

const SubmitBtn = styled.button`
  width: 100%;
  padding: 12px;
  border-radius: 30px;
  border: none;
  cursor: pointer;
  background: ${BRAND_GRADIENT};
  color: #fff;
`;

/* ================= COMPONENT ================= */

const Careers = () => {
  const [showForm, setShowForm] = useState(false);
  const [jobRole, setJobRole] = useState("");

  const openForm = (role) => {
    setJobRole(role);
    setShowForm(true);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const f = e.target;

    const message = `
New Job Application

Name: ${f.name.value}
Email: ${f.email.value}
Mobile: ${f.mobile.value}

MS Teams ID: ${f.teams.value}
GitHub ID: ${f.github.value}
LinkedIn ID: ${f.linkedin.value}

Role: ${jobRole}
Job Type: ${f.type.value}
Experience: ${f.experience.value}
Availability: ${f.availability.value}
Location: ${f.location.value}
    `;

    const url = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(message)}`;
    window.open(url, "_blank");
    setShowForm(false);
  };

  return (
    <Wrapper>
      <Container>
        <Section
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <Title>BerryBloomers Careers</Title>
          <Paragraph>
            Apply now and get onboarded for discussions, projects and real-world
            experience.
          </Paragraph>
        </Section>

        <JobsGrid>
          {[
            {
              role: "React.js Intern",
              desc: "Build interactive UI components, work with APIs, and improve frontend performance using React and Tailwind.",
            },
            {
              role: "Java Intern",
              desc: "Work on backend services, REST APIs, and database integration using Spring Boot and Java.",
            },
            {
              role: "AI Intern",
              desc: "Assist in building machine learning models, data analysis, and AI-based automation solutions.",
            },
            {
              role: "React Native Developer (Experienced)",
              desc: "Develop cross-platform mobile applications and optimize app performance for Android and iOS.",
            },
          ].map((job) => (
            <JobCard key={job.role} whileHover={{ scale: 1.04 }}>
              <JobTitle>{job.role}</JobTitle>
              <JobDesc>{job.desc}</JobDesc>
              <BtnRow>
                <ApplyBtn onClick={() => openForm(job.role)}>Apply</ApplyBtn>
                <WhatsAppBtn
                  href={`https://wa.me/${WHATSAPP_NUMBER}?text=Hi, I want to apply for ${job.role}`}
                  target="_blank"
                >
                  WhatsApp
                </WhatsAppBtn>
              </BtnRow>
            </JobCard>
          ))}
        </JobsGrid>

        {showForm && (
          <Overlay>
            <Modal>
              <h3 style={{ marginBottom: 16 }}>Apply for {jobRole}</h3>

              <form onSubmit={handleSubmit}>
                <Input name="name" placeholder="Full Name" required />
                <Input name="email" type="email" placeholder="Email" required />
                <Input
                  name="mobile"
                  placeholder="Mobile / WhatsApp Number"
                  required
                />

                <Input
                  name="teams"
                  placeholder="MS Teams Account ID"
                  required
                />
                <Input
                  name="github"
                  placeholder="GitHub Profile ID / URL"
                  required
                />
                <Input
                  name="linkedin"
                  placeholder="LinkedIn Profile URL"
                  required
                />

                <Select name="type" required>
                  <option value="">Job Type</option>
                  <option>Part Time</option>
                  <option>Full Time</option>
                  <option>Internship</option>
                </Select>

                <Select name="experience" required>
                  <option value="">Experience Level</option>
                  <option>Fresher</option>
                  <option>0-1 Year</option>
                  <option>1-3 Years</option>
                  <option>3+ Years</option>
                </Select>

                <Select name="availability" required>
                  <option value="">Availability</option>
                  <option>Immediate</option>
                  <option>15 Days</option>
                  <option>30 Days</option>
                </Select>

                <Input name="location" placeholder="City, Country" required />

                <SubmitBtn type="submit">Submit & Send to WhatsApp</SubmitBtn>
              </form>

              <p
                onClick={() => setShowForm(false)}
                style={{
                  marginTop: 14,
                  textAlign: "center",
                  cursor: "pointer",
                  color: "#94a3b8",
                }}
              >
                Cancel
              </p>
            </Modal>
          </Overlay>
        )}
      </Container>
    </Wrapper>
  );
};

export default Careers;
